import {
  AlertCircle,
  CheckCircle,
  CreditCard,
  Loader2,
  Send,
  Clock,
  Info,
  FileCheck,
  Download,
  FileCode,
  RefreshCw,
  Trash2,
  Building2,
  FileJson,
  ExternalLink,
  X,
  KeyRound,
  Eye,
  EyeOff,
  Users,
  FileSearch,
  PencilLine,
} from 'lucide-react'
import CT600PreviewModal from './CT600PreviewModal'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CT600SubmissionForm } from '../../types/ct600Filing'
import type { CT600WizardStepId } from '../../types/ct600Filing'
import type {
  Ct600Director,
  Ct600PeriodSubmission,
  Ct600Status,
} from '../../types'
import { toast } from 'sonner'
import ct600Service from '../../utils/ct600Service'
import HmrcStatusModal from './HmrcStatusModal'

function fmtDateTime(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

/** Save a downloaded file. */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Documents for one accepted period. On a long period of account each period
 * is its own return, so these stay available even when the other period was
 * rejected and the filing as a whole shows "Rejected".
 */
function PeriodDocumentActions({
  formId,
  ps,
}: {
  formId: string
  ps: Ct600PeriodSubmission
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const period = ps.periodIndex
  const tag = `${formId.slice(-6)}-P${period}`

  const run = async (key: string, fetchFile: () => Promise<Blob>, filename: string) => {
    setBusy(key)
    try {
      saveBlob(await fetchFile(), filename)
    } catch (err) {
      console.error(`Failed to download ${key}:`, err)
      toast.error(err instanceof Error && err.message ? err.message : 'Download failed.')
    } finally {
      setBusy(null)
    }
  }

  const actions: Array<{ key: string; label: string; icon: typeof Download; onClick: () => void }> = [
    {
      key: 'receipt',
      label: 'Receipt',
      icon: FileCheck,
      onClick: () => run('receipt', () => ct600Service.downloadReceipt(formId, period), `CT600-Receipt-${tag}.pdf`),
    },
    {
      key: 'return',
      label: 'CT600 form',
      icon: FileSearch,
      onClick: () => run('return', () => ct600Service.downloadPeriodReturn(formId, period), `CT600-${tag}.pdf`),
    },
    {
      key: 'computation',
      label: 'Tax computation',
      icon: FileCode,
      onClick: () =>
        run('computation', () => ct600Service.downloadPeriodComputation(formId, period), `CT600-Computation-${tag}.html`),
    },
    {
      key: 'all',
      label: 'Download all',
      icon: Download,
      onClick: () => run('all', () => ct600Service.downloadPeriodDocuments(formId, period), `CT600-Documents-${tag}.zip`),
    },
  ]

  return (
    <div className='mt-3 flex flex-wrap gap-2'>
      {actions.map(({ key, label, icon: Icon, onClick }) => (
        <button
          key={key}
          type='button'
          onClick={onClick}
          disabled={busy !== null}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-medium disabled:opacity-50 ${
            key === 'all'
              ? 'bg-[#001B38] text-white hover:bg-[#002a50]'
              : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {busy === key ? <Loader2 className='h-3 w-3 animate-spin' /> : <Icon className='h-3 w-3' />}
          {label}
        </button>
      ))}
    </div>
  )
}

/**
 * Draft CT600 on HMRC's own form, built exactly as it would be filed. A long
 * period of account is two returns, so there's one per period.
 */
function DraftCt600Download({ formId, isLpoa }: { formId: string; isLpoa: boolean }) {
  const [busy, setBusy] = useState<1 | 2 | null>(null)
  const download = async (period: 1 | 2) => {
    setBusy(period)
    try {
      saveBlob(await ct600Service.downloadPeriodReturn(formId, period), `CT600-draft-${formId.slice(-6)}-P${period}.pdf`)
    } catch (err) {
      console.error('Failed to download CT600:', err)
      toast.error(err instanceof Error && err.message ? err.message : 'Could not build the CT600 PDF.')
    } finally {
      setBusy(null)
    }
  }
  const periods: Array<1 | 2> = isLpoa ? [1, 2] : [1]
  return (
    <>
      {periods.map(period => (
        <button
          key={period}
          type='button'
          onClick={() => download(period)}
          disabled={busy !== null}
          className='flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50'
        >
          {busy === period ? <Loader2 className='h-4 w-4 animate-spin' /> : <Download className='h-4 w-4' />}
          {isLpoa ? `Period ${period} PDF` : 'Download PDF'}
        </button>
      ))}
    </>
  )
}

function StatusPill({ status }: { status: string | undefined }) {
  const s = (status ?? '').toLowerCase()
  const base =
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold'
  if (s === 'accepted')
    return (
      <span className={`${base} bg-green-100 text-green-800`}>
        <CheckCircle className='h-3 w-3' />
        Accepted
      </span>
    )
  if (s === 'rejected')
    return (
      <span className={`${base} bg-red-100 text-red-800`}>
        <X className='h-3 w-3' />
        Rejected
      </span>
    )
  if (s === 'processing')
    return (
      <span className={`${base} bg-amber-100 text-amber-800`}>
        <Clock className='h-3 w-3' />
        Processing
      </span>
    )
  if (s === 'submitted')
    return (
      <span className={`${base} bg-blue-100 text-blue-800`}>
        <Send className='h-3 w-3' />
        Submitted
      </span>
    )
  if (s === 'partial')
    return (
      <span className={`${base} bg-amber-100 text-amber-800`}>
        <AlertCircle className='h-3 w-3' />
        Partial
      </span>
    )
  return (
    <span className={`${base} bg-gray-100 text-gray-700`}>
      {status ?? 'Unknown'}
    </span>
  )
}

function PeriodSubmissionRow({
  ps,
  formId,
}: {
  ps: Ct600PeriodSubmission
  formId?: string | null
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4'>
      <div className='flex items-start justify-between gap-3 mb-2'>
        <div>
          <p className='text-sm font-semibold text-gray-900'>
            {ps.periodLabel ?? `Period ${ps.periodIndex}`}
          </p>
          {(ps.periodStart || ps.periodEnd) && (
            <p className='text-[11px] text-gray-500'>
              {fmtDateTime(ps.periodStart)} → {fmtDateTime(ps.periodEnd)}
            </p>
          )}
        </div>
        <StatusPill status={ps.status} />
      </div>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-gray-600'>
        {ps.correlationId && (
          <div>
            <span className='text-gray-500'>Correlation ID:</span>{' '}
            <span className='font-mono break-all'>{ps.correlationId}</span>
          </div>
        )}
        {ps.hmrcReference && (
          <div>
            <span className='text-gray-500'>HMRC Reference:</span>{' '}
            <span className='font-mono break-all'>{ps.hmrcReference}</span>
          </div>
        )}
        {ps.irMark && (
          <div>
            <span className='text-gray-500'>IRmark:</span>{' '}
            <span className='font-mono break-all'>{ps.irMark}</span>
          </div>
        )}
        {ps.submittedAt && (
          <div>
            <span className='text-gray-500'>Submitted:</span>{' '}
            {fmtDateTime(ps.submittedAt)}
          </div>
        )}
        {ps.acceptedAt && (
          <div>
            <span className='text-gray-500'>Accepted:</span>{' '}
            {fmtDateTime(ps.acceptedAt)}
          </div>
        )}
        {ps.lastPolledAt && (
          <div>
            <span className='text-gray-500'>Last polled:</span>{' '}
            {fmtDateTime(ps.lastPolledAt)}
          </div>
        )}
        {typeof ps.accountsAttached === 'boolean' && (
          <div>
            <span className='text-gray-500'>Accounts attached:</span>{' '}
            {ps.accountsAttached ? 'Yes' : 'No (filed with separate return)'}
          </div>
        )}
      </div>
      {ps.hmrcReceiptText && (
        <p className='mt-2 rounded bg-gray-50 p-2 text-[11px] text-gray-700 italic leading-relaxed'>
          {ps.hmrcReceiptText}
        </p>
      )}
      {Array.isArray(ps.errors) && ps.errors.length > 0 && (
        // `overflow-hidden` + `min-w-0` on the container prevent any flex
        // descendant from pushing the box wider than its grid cell. Errors
        // commonly contain raw JSON with long URLs and no whitespace, so the
        // message span needs `break-all` (we'd rather wrap mid-URL than burst
        // the layout).
        <div className='mt-2 overflow-hidden rounded-lg border border-red-200 bg-red-50 p-2'>
          <p className='text-[11px] font-semibold text-red-900 mb-1'>
            HMRC errors
          </p>
          <ul className='space-y-1.5'>
            {ps.errors.map((e, i) => (
              <li
                key={i}
                className='flex items-start gap-2 text-[11px] text-red-800 min-w-0'
              >
                <span className='font-mono rounded bg-red-100 px-1 py-0.5 text-[10px] text-red-700 shrink-0'>
                  {e.code}
                </span>
                <span className='min-w-0 flex-1 font-mono text-[10.5px] leading-relaxed [overflow-wrap:anywhere] break-all'>
                  {e.message}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {formId && ps.status === 'accepted' && <PeriodDocumentActions formId={formId} ps={ps} />}
    </div>
  )
}

interface Props {
  submission: CT600SubmissionForm
  onSubmissionChange: (update: CT600SubmissionForm) => void
  isHmrcAuthorized?: boolean
  onOpenHmrcAuth?: () => void
  isSubmitting: boolean
  isPollingStatus?: boolean
  submissionResult: {
    status: string
    correlationId?: string
    message?: string
  } | null
  onSubmit: () => void
  hasCalculated: boolean
  isPaid: boolean
  selectedPlan: string | null
  formStatus?: Ct600Status | null
  formId?: string | null
  readyForSubmission?: boolean
  validationErrors?: string[]
  onGoToStep?: (step: CT600WizardStepId) => void
  /** Active directors fetched from period-split (used to pre-fill the submitter dropdown). */
  directors?: Ct600Director[]
  /** Per-period HMRC submissions, populated for both single-period and LPOA filings. */
  periodSubmissions?: Ct600PeriodSubmission[]
  /** Aggregate status across all periods (from latest submit/poll response). */
  aggregateStatus?: string | null
  /** True when the form is a Long Period of Account split into two CT600s. */
  isLpoa?: boolean
  /** Manually trigger a status poll (the parent also auto-polls on a 30s cadence). */
  onPollStatus?: () => void
  /** Shown in the CT600 preview header so the user can see whose return it is. */
  companyName?: string
  periodStart?: string
  periodEnd?: string
  /** The user is amending a return HMRC has already accepted. */
  amendMode?: boolean
  /** Start amending an accepted return; omitted where amending isn't offered. */
  onStartAmendment?: () => void
  onCancelAmendment?: () => void
}

// Map error messages to wizard steps based on keywords
function getStepFromError(error: string): CT600WizardStepId | null {
  const lower = error.toLowerCase()
  if (
    lower.includes('company information') ||
    lower.includes('company name') ||
    lower.includes('company number')
  )
    return 2
  if (
    lower.includes('financial statement') ||
    lower.includes('turnover') ||
    lower.includes('profit') ||
    lower.includes('balance sheet')
  )
    return 3
  if (lower.includes('tax computation') || lower.includes('tax calc')) return 4
  if (lower.includes('supplementary')) return 5
  if (lower.includes('review') || lower.includes('calculat')) return 6
  if (
    lower.includes('declaration') ||
    lower.includes('submission') ||
    lower.includes('submitter') ||
    lower.includes('submitted by')
  )
    return 7
  return null
}

// Step labels for display
const stepLabels: Record<number, string> = {
  1: 'Company & Period Setup',
  2: 'Company Information',
  3: 'Financial Statements',
  4: 'Tax Adjustments',
  5: 'Supplementary Pages',
  6: 'Tax Computation',
  7: 'Declaration & Submit',
}

// ── Reusable result modal ──
function ResultModal({
  title,
  subtitle,
  data,
  onClose,
  variant = 'default',
}: {
  title: string
  subtitle?: string
  data: Record<string, unknown>
  onClose: () => void
  variant?: 'default' | 'success' | 'danger'
}) {
  // Try to extract meaningful fields from the data
  const inner = (data as any)?.data ?? data
  const success = inner?.success
  const rawStatus = inner?.status ?? (data as any)?.status
  const rawMessage = inner?.message ?? (data as any)?.message
  const transactionId = inner?.transactionId
  const submissionNumber = inner?.submissionNumber
  const errors: string[] = Array.isArray(inner?.errors) ? inner.errors : []

  // Companies House sometimes leaves the polling status at "pending" even
  // after returning an explicit ACCEPT/REJECT indicator. Treat the indicator
  // as authoritative so the UI matches reality.
  const acceptIndicator = String(
    inner?.accept_reject_indicator ??
      inner?.acceptRejectIndicator ??
      (data as any)?.accept_reject_indicator ??
      rawMessage ??
      '',
  )
    .toLowerCase()
    .trim()
  const indicatesAccepted =
    acceptIndicator === 'accept' || acceptIndicator === 'accepted'
  const indicatesRejected =
    acceptIndicator === 'reject' || acceptIndicator === 'rejected'

  const status = indicatesAccepted
    ? 'accepted'
    : indicatesRejected
      ? 'rejected'
      : rawStatus
  // Suppress the raw "ACCEPT" / "REJECT" string — it's already shown as the pill.
  const message = indicatesAccepted || indicatesRejected ? undefined : rawMessage

  const isAcceptedState = variant === 'success' || success || indicatesAccepted
  const isRejectedState = variant === 'danger' || indicatesRejected

  const headerColor = isRejectedState
    ? 'bg-red-50 border-red-200'
    : isAcceptedState
      ? 'bg-green-50 border-green-200'
      : status === 'pending'
        ? 'bg-amber-50 border-amber-200'
        : 'bg-gray-50 border-gray-200'

  const iconColor = isRejectedState
    ? 'text-red-600'
    : isAcceptedState
      ? 'text-green-600'
      : status === 'pending'
        ? 'text-amber-600'
        : 'text-gray-600'

  const StatusIcon = isRejectedState
    ? Trash2
    : isAcceptedState
      ? CheckCircle
      : status === 'pending'
        ? Clock
        : Info

  const modal = (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center p-4'>
      <div className='absolute inset-0 bg-black/50' onClick={onClose} />
      <div className='relative z-10 w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl'>
        {/* Header */}
        <div className='flex items-center justify-between rounded-t-2xl border-b border-gray-200 bg-white px-6 py-4 shrink-0'>
          <div>
            <h2 className='text-base font-bold text-gray-900'>{title}</h2>
            {subtitle && (
              <p className='mt-0.5 text-xs text-gray-500'>{subtitle}</p>
            )}
          </div>
          <button
            type='button'
            onClick={onClose}
            className='flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-500'
          >
            <X className='h-4 w-4' />
          </button>
        </div>

        {/* Body */}
        <div className='overflow-y-auto flex-1 p-6 space-y-4'>
          {/* Status banner */}
          <div
            className={`flex items-center gap-3 rounded-xl border-2 p-4 ${headerColor}`}
          >
            <StatusIcon className={`h-6 w-6 shrink-0 ${iconColor}`} />
            <div className='flex-1'>
              {status && (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                    isRejectedState
                      ? 'bg-red-100 text-red-800'
                      : isAcceptedState
                        ? 'bg-green-100 text-green-800'
                        : status === 'pending'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {String(status).charAt(0).toUpperCase() +
                    String(status).slice(1)}
                </span>
              )}
              {message && (
                <p className='mt-1 text-sm text-gray-700'>{String(message)}</p>
              )}
              {!status && !message && success !== undefined && (
                <p className='text-sm font-medium text-gray-900'>
                  {success
                    ? 'Operation completed successfully'
                    : 'Operation completed'}
                </p>
              )}
            </div>
          </div>

          {/* Key details */}
          <div className='rounded-xl border border-gray-200 bg-white p-4'>
            <p className='mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500'>
              Details
            </p>
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
              {transactionId && (
                <div className='flex flex-col gap-0.5'>
                  <span className='text-xs text-gray-500'>Transaction ID</span>
                  <span className='text-sm font-medium text-gray-900 break-all font-mono'>
                    {String(transactionId)}
                  </span>
                </div>
              )}
              {submissionNumber && (
                <div className='flex flex-col gap-0.5'>
                  <span className='text-xs text-gray-500'>
                    Submission Number
                  </span>
                  <span className='text-sm font-medium text-gray-900 break-all font-mono'>
                    {String(submissionNumber)}
                  </span>
                </div>
              )}
              {/* Render remaining scalar fields */}
              {Object.entries(inner).map(([key, val]) => {
                if (
                  [
                    'success',
                    'status',
                    'message',
                    'transactionId',
                    'submissionNumber',
                    'errors',
                    'data',
                  ].includes(key)
                )
                  return null
                if (
                  val === null ||
                  val === undefined ||
                  typeof val === 'object'
                )
                  return null
                return (
                  <div key={key} className='flex flex-col gap-0.5'>
                    <span className='text-xs text-gray-500'>
                      {key
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, s => s.toUpperCase())}
                    </span>
                    <span className='text-sm font-medium text-gray-900 break-all'>
                      {String(val)}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className='rounded-xl border border-red-200 bg-red-50 p-4'>
              <p className='mb-2 text-xs font-semibold text-red-900'>Errors</p>
              <ul className='space-y-1'>
                {errors.map((err, i) => (
                  <li
                    key={i}
                    className='flex items-start gap-2 text-xs text-red-800'
                  >
                    <span className='mt-1 h-1.5 w-1.5 rounded-full bg-red-400 shrink-0' />
                    {typeof err === 'string' ? err : JSON.stringify(err)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-3 shrink-0'>
          <button
            type='button'
            onClick={onClose}
            className='w-full rounded-lg bg-[#001B38] py-2.5 text-sm font-medium text-white hover:bg-[#002a50] transition-colors'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

export default function CT600StepSubmit({
  submission,
  onSubmissionChange,
  isSubmitting,
  submissionResult,
  onSubmit,
  hasCalculated,
  isPaid,
  selectedPlan,
  formStatus,
  formId,
  readyForSubmission = true,
  validationErrors = [],
  onGoToStep,
  directors = [],
  periodSubmissions = [],
  aggregateStatus,
  isLpoa = false,
  isPollingStatus = false,
  onPollStatus,
  companyName,
  periodStart,
  periodEnd,
  amendMode = false,
  onStartAmendment,
  onCancelAmendment,
}: Props) {
  const [submittedByMode, setSubmittedByMode] = useState<'select' | 'custom'>(
    directors.length > 0 ? 'select' : 'custom'
  )
  // Directors arrive asynchronously; sync mode until the user manually toggles.
  const submittedByModeUserToggledRef = useRef(false)
  useEffect(() => {
    if (submittedByModeUserToggledRef.current) return
    setSubmittedByMode(directors.length > 0 ? 'select' : 'custom')
  }, [directors.length])
  const [isGeneratingIxbrl, setIsGeneratingIxbrl] = useState(false)
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false)
  const [showGatewayPassword, setShowGatewayPassword] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  // Post-submission action state
  const [isPollingHmrcStatus, setIsPollingHmrcStatus] = useState(false)
  const [hmrcStatusResult, setHmrcStatusResult] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [hmrcStatusError, setHmrcStatusError] = useState<string | null>(null)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeletingSubmission, setIsDeletingSubmission] = useState(false)
  const [deletionResult, setDeletionResult] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [deletionError, setDeletionError] = useState<string | null>(null)
  const [isExportingXml, setIsExportingXml] = useState(false)
  const [isFilingAccounts, setIsFilingAccounts] = useState(false)
  const [chAccountsResult, setChAccountsResult] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [isPollingChStatus, setIsPollingChStatus] = useState(false)
  const [chStatusResult, setChStatusResult] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [companyAuthCode, setCompanyAuthCode] = useState('')
  const [showChResultModal, setShowChResultModal] = useState(false)
  const [showChStatusModal, setShowChStatusModal] = useState(false)
  const [showDeletionResultModal, setShowDeletionResultModal] = useState(false)

  // Post-submission handlers
  const handlePollHmrcStatus = async () => {
    if (!formId) return
    setIsPollingHmrcStatus(true)
    setHmrcStatusError(null)
    try {
      const result = await ct600Service.getSubmissionStatus(formId)
      setHmrcStatusResult(result.response as unknown as Record<string, unknown>)
      setShowStatusModal(true)
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to poll HMRC status'
      setHmrcStatusError(msg)
    } finally {
      setIsPollingHmrcStatus(false)
    }
  }

  const handleDeleteSubmission = async () => {
    if (!formId) return
    setIsDeletingSubmission(true)
    setDeletionError(null)
    try {
      const result = await ct600Service.deleteSubmission(formId)
      setDeletionResult(result.response as Record<string, unknown>)
      setShowDeleteConfirm(false)
      setShowDeletionResultModal(true)

      // Backend flips the form to `completed` once every period's delete is
      // acknowledged. Re-poll so the parent UI reflects that without the user
      // having to refresh the page.
      if (onPollStatus) {
        onPollStatus()
      }
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to delete submission'
      setDeletionError(msg)
      setShowDeleteConfirm(false)
    } finally {
      setIsDeletingSubmission(false)
    }
  }

  const handleExportXmlArtifacts = async () => {
    if (!formId) return
    setIsExportingXml(true)
    try {
      // Streamed by the backend — for LPOA it includes per-period sub-folders.
      saveBlob(await ct600Service.downloadXmlArtifactsZip(formId), `CT600-XML-Artifacts-${formId}.zip`)
    } catch (err) {
      console.error('Failed to export XML artifacts:', err)
      toast.error('Failed to export XML artifacts.')
    } finally {
      setIsExportingXml(false)
    }
  }

  const handleFileAccounts = async () => {
    if (!formId) return
    setIsFilingAccounts(true)
    try {
      const result = await ct600Service.fileAccounts(formId, {
        companyAuthCode,
      })
      setChAccountsResult(result.response as Record<string, unknown>)
      setShowChResultModal(true)
    } catch (err: any) {
      const msg =
        err?.response?.message ||
        err?.message ||
        'Failed to file accounts to Companies House'
      toast.error(msg)
    } finally {
      setIsFilingAccounts(false)
    }
  }

  const handlePollChStatus = async () => {
    if (!formId) return
    setIsPollingChStatus(true)
    try {
      const result = await ct600Service.getAccountsFilingStatus(formId)
      setChStatusResult(result.response as Record<string, unknown>)
      setShowChStatusModal(true)
    } catch (err: any) {
      const msg =
        err?.response?.message ||
        err?.message ||
        'Failed to check Companies House status'
      toast.error(msg)
    } finally {
      setIsPollingChStatus(false)
    }
  }

  // Fetch iXBRL and return the raw HTML text
  const fetchIxbrlHtml = async (): Promise<string | null> => {
    if (!formId) return null
    setIsGeneratingIxbrl(true)
    try {
      const result = await ct600Service.generateIxbrl(formId)
      // result may be a Blob or raw text — normalise to string
      if (result instanceof Blob) return await result.text()
      if (typeof result === 'string') return result
      return String(result)
    } catch (err) {
      console.error('Failed to generate iXBRL:', err)
      return null
    } finally {
      setIsGeneratingIxbrl(false)
    }
  }

  const handleViewIxbrl = async () => {
    const html = await fetchIxbrlHtml()
    if (!html) return
    // Force text/html so the browser renders it instead of showing XML source
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 30000)
  }

  const handleDownloadIxbrl = async () => {
    const html = await fetchIxbrlHtml()
    if (!html) return
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `CT600-iXBRL-${formId}.html`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const IxbrlDownloadButton = () => {
    if (!formId) return null
    return (
      <div className='rounded-lg border border-gray-200 bg-white p-5'>
        <div className='flex items-start gap-3 mb-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 shrink-0'>
            <FileCode className='h-5 w-5 text-gray-600' />
          </div>
          <div>
            <h3 className='text-sm font-semibold text-gray-900'>
              iXBRL Document
            </h3>
            <p className='mt-0.5 text-xs text-gray-500'>
              View or download your CT600 return as a formatted iXBRL document.
            </p>
          </div>
        </div>
        <div className='flex items-center gap-2 flex-wrap'>
          <button
            onClick={handleViewIxbrl}
            disabled={isGeneratingIxbrl}
            className='flex items-center gap-2 rounded-lg bg-[#001B38] px-4 py-2 text-sm font-medium text-white hover:bg-[#002a50] transition-colors disabled:opacity-50'
          >
            {isGeneratingIxbrl ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Loading...
              </>
            ) : (
              <>
                <FileCode className='h-4 w-4' />
                View
              </>
            )}
          </button>
          <button
            onClick={handleDownloadIxbrl}
            disabled={isGeneratingIxbrl}
            className='flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50'
          >
            <Download className='h-4 w-4' />
            Download
          </button>
        </div>
      </div>
    )
  }
  const handleDownloadReceipt = async () => {
    if (!formId) return
    setIsDownloadingReceipt(true)
    try {
      const blob = await ct600Service.downloadReceipt(formId)
      const url = URL.createObjectURL(
        blob instanceof Blob
          ? blob
          : new Blob([blob], { type: 'application/pdf' })
      )
      const a = document.createElement('a')
      a.href = url
      a.download = `CT600-Receipt-${formId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download receipt:', err)
    } finally {
      setIsDownloadingReceipt(false)
    }
  }

  const ReceiptDownloadButton = () => {
    if (!formId) return null
    return (
      <div className='rounded-lg border border-gray-200 bg-white p-5'>
        <div className='flex items-start gap-3 mb-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 shrink-0'>
            <FileCheck className='h-5 w-5 text-gray-600' />
          </div>
          <div>
            <h3 className='text-sm font-semibold text-gray-900'>
              Submission Receipt
            </h3>
            <p className='mt-0.5 text-xs text-gray-500'>
              Download your CT600 submission receipt as a PDF document.
            </p>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          <button
            onClick={handleDownloadReceipt}
            disabled={isDownloadingReceipt}
            className='flex items-center gap-2 rounded-lg bg-[#001B38] px-4 py-2 text-sm font-medium text-white hover:bg-[#002a50] transition-colors disabled:opacity-50'
          >
            {isDownloadingReceipt ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Downloading...
              </>
            ) : (
              <>
                <Download className='h-4 w-4' />
                Download
              </>
            )}
          </button>
        </div>
      </div>
    )
  }

  // Per-period status panel — surfaced for both LPOA and single-period filings
  // once we have any tracked periodSubmissions. Always shown for LPOA so the
  // user can see AP1/AP2 split clearly.
  const showPeriodPanel = periodSubmissions.length > 0
  const periodStatusPanel = showPeriodPanel ? (
    <div className='rounded-lg border border-gray-200 bg-white p-5'>
      <div className='mb-3 flex items-start justify-between gap-3'>
        <div>
          <h3 className='text-sm font-semibold text-gray-900'>
            {isLpoa ? 'HMRC Submissions (AP1 + AP2)' : 'HMRC Submission'}
          </h3>
          <p className='mt-0.5 text-xs text-gray-500'>
            {isLpoa
              ? 'This is a Long Period of Account — two CT600 returns were sent to HMRC.'
              : 'Per-period status from HMRC.'}
          </p>
        </div>
        <div className='flex items-center gap-2'>
          {aggregateStatus && (
            <div className='flex items-center gap-1.5'>
              <span className='text-[11px] text-gray-500'>Aggregate:</span>
              <StatusPill status={aggregateStatus} />
            </div>
          )}
          {onPollStatus && (
            <button
              type='button'
              onClick={onPollStatus}
              disabled={isPollingStatus}
              className='flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50'
            >
              {isPollingStatus ? (
                <Loader2 className='h-3 w-3 animate-spin' />
              ) : (
                <RefreshCw className='h-3 w-3' />
              )}
              {isPollingStatus ? 'Polling…' : 'Refresh'}
            </button>
          )}
        </div>
      </div>
      <div
        className={`grid gap-3 ${periodSubmissions.length > 1 ? 'lg:grid-cols-2' : 'grid-cols-1'}`}
      >
        {periodSubmissions.map(ps => (
          <PeriodSubmissionRow key={ps.periodIndex} ps={ps} formId={formId ?? null} />
        ))}
      </div>
    </div>
  ) : null

  const deleteSubmissionPanel = formId ? (
    <div className='rounded-lg border border-red-100 bg-white p-4'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 shrink-0'>
            <Trash2 className='h-4 w-4 text-red-600' />
          </div>
          <div>
            <p className='text-sm font-medium text-gray-900 text-start'>
              Delete Submission
            </p>
            <p className='text-xs text-gray-500'>
              Remove this submission from HMRC
            </p>
          </div>
        </div>
        {!showDeleteConfirm && !deletionResult && (
          <button
            type='button'
            onClick={() => setShowDeleteConfirm(true)}
            className='flex items-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 transition-colors shrink-0 ml-12 sm:ml-0'
          >
            <Trash2 className='h-4 w-4' />
            Delete
          </button>
        )}
      </div>
      {showDeleteConfirm && !deletionResult && (
        <div className='mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-lg bg-red-50 p-3'>
          <div className='flex items-start gap-2 flex-1'>
            <AlertCircle className='h-4 w-4 text-red-600 shrink-0 mt-0.5' />
            <p className='flex-1 text-xs text-red-800'>
              This will delete the submission from HMRC. Are you sure?
            </p>
          </div>
          <div className='flex items-center gap-2 shrink-0'>
            <button
              type='button'
              onClick={handleDeleteSubmission}
              disabled={isDeletingSubmission}
              className='flex items-center gap-1.5 rounded-lg bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 transition-colors disabled:opacity-50'
            >
              {isDeletingSubmission ? (
                <Loader2 className='h-3 w-3 animate-spin' />
              ) : null}
              Confirm Delete
            </button>
            <button
              type='button'
              onClick={() => setShowDeleteConfirm(false)}
              className='rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors'
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {deletionError && (
        <p className='mt-3 rounded-lg bg-red-50 p-3 text-xs text-red-700'>
          {deletionError}
        </p>
      )}
      {deletionResult && !showDeletionResultModal && (
        <div className='mt-3 flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2.5'>
          <p className='text-xs text-gray-600'>Deletion result available</p>
          <button
            type='button'
            onClick={() => setShowDeletionResultModal(true)}
            className='text-xs font-medium text-[#001B38] hover:underline'
          >
            View details →
          </button>
        </div>
      )}
    </div>
  ) : null

  const amendedReturnPanel = amendMode ? (
    <div className='rounded-lg border border-amber-200 bg-amber-50 p-4'>
      <div className='flex items-start gap-3'>
        <PencilLine className='h-5 w-5 flex-shrink-0 text-amber-600 mt-0.5' />
        <div>
          <span className='text-sm font-medium text-amber-900'>
            Amended return
          </span>
          <p className='mt-0.5 text-xs text-amber-800 leading-relaxed'>
            This will be sent to HMRC as an amendment to the return already
            accepted for this period.
          </p>
        </div>
      </div>
    </div>
  ) : (
    <div className='rounded-lg border border-gray-200 bg-white p-4'>
      <label className='flex items-start gap-3 cursor-pointer'>
        <input
          type='checkbox'
          checked={submission.amendedReturn ?? false}
          onChange={e => update('amendedReturn', e.target.checked)}
          className='mt-0.5 h-5 w-5 rounded border-gray-300 text-gray-900 focus:ring-gray-900'
        />
        <div>
          <span className='text-sm font-medium text-gray-900'>
            Amended return
          </span>
          <p className='mt-0.5 text-xs text-gray-500 leading-relaxed'>
            Tick this if you are submitting an amendment to a return HMRC has
            already received for this period.
          </p>
        </div>
      </label>
    </div>
  )

  // Companies House panel — available before *and* after HMRC submission,
  // because filing accounts to CH is independent of submitting CT600 to HMRC.
  const companiesHousePanel = formId ? (
    <div className='rounded-lg border border-gray-200 bg-white p-4'>
      <div className='flex items-center gap-3 mb-3'>
        <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 shrink-0'>
          <Building2 className='h-4 w-4 text-indigo-600' />
        </div>
        <div>
          <p className='text-sm font-medium text-gray-900 text-start'>
            Companies House
          </p>
          <p className='text-xs text-gray-500'>
            Submit accounts and track filing status
          </p>
        </div>
      </div>
      <div className='mb-3'>
        <label className='mb-1.5 block text-xs font-medium text-gray-700'>
          Company Authentication Code
        </label>
        <input
          type='text'
          value={companyAuthCode}
          onChange={e => setCompanyAuthCode(e.target.value)}
          placeholder='6–8 alphanumeric characters'
          maxLength={8}
          className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
        />
      </div>
      <div className='flex flex-col gap-2 sm:flex-row'>
        <button
          type='button'
          onClick={handleFileAccounts}
          disabled={isFilingAccounts || companyAuthCode.trim().length < 6}
          className='flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#001B38] px-4 py-2 text-sm font-medium text-white hover:bg-[#002a50] transition-colors disabled:opacity-50'
        >
          {isFilingAccounts ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <Send className='h-4 w-4' />
          )}
          {isFilingAccounts ? 'Filing...' : 'File Accounts to CH'}
        </button>
        <button
          type='button'
          onClick={handlePollChStatus}
          disabled={isPollingChStatus}
          className='flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50'
        >
          {isPollingChStatus ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <RefreshCw className='h-4 w-4' />
          )}
          {isPollingChStatus ? 'Checking...' : 'Track CH Status'}
        </button>
      </div>
      {chAccountsResult && !showChResultModal && (
        <div className='mt-3 flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2.5'>
          <p className='text-xs text-gray-600'>Filing result available</p>
          <button
            type='button'
            onClick={() => setShowChResultModal(true)}
            className='text-xs font-medium text-[#001B38] hover:underline'
          >
            View details →
          </button>
        </div>
      )}
      {chStatusResult && !showChStatusModal && (
        <div className='mt-3 flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2.5'>
          <p className='text-xs text-gray-600'>Status result available</p>
          <button
            type='button'
            onClick={() => setShowChStatusModal(true)}
            className='text-xs font-medium text-[#001B38] hover:underline'
          >
            View details →
          </button>
        </div>
      )}
    </div>
  ) : null

  const postSubmissionActions = formId ? (
    <div className='space-y-3'>
      <h3 className='text-sm font-semibold text-gray-900'>
        Post-Submission Actions
      </h3>

      {/* Track HMRC Submission Status */}
      <div className='rounded-lg border border-gray-200 bg-white p-4'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
          <div className='flex items-center gap-3'>
            <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 shrink-0'>
              <RefreshCw className='h-4 w-4 text-blue-600' />
            </div>
            <div>
              <p className='text-sm font-medium text-gray-900 text-start'>
                Track HMRC Status
              </p>
              <p className='text-xs text-gray-500'>
                Poll HMRC for the latest submission status
              </p>
            </div>
          </div>
          <button
            type='button'
            onClick={handlePollHmrcStatus}
            disabled={isPollingHmrcStatus}
            className='flex items-center gap-2 rounded-lg bg-[#001B38] px-4 py-2 text-sm font-medium text-white hover:bg-[#002a50] transition-colors disabled:opacity-50 shrink-0 ml-12 sm:ml-0'
          >
            {isPollingHmrcStatus ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <RefreshCw className='h-4 w-4' />
            )}
            {isPollingHmrcStatus ? 'Polling...' : 'Check Status'}
          </button>
        </div>
        {hmrcStatusError && (
          <p className='mt-3 rounded-lg bg-red-50 p-3 text-xs text-red-700'>
            {hmrcStatusError}
          </p>
        )}
        {hmrcStatusResult && !isPollingHmrcStatus && (
          <div className='mt-3 flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2.5'>
            <p className='text-xs text-gray-600'>Status result available</p>
            <button
              type='button'
              onClick={() => setShowStatusModal(true)}
              className='text-xs font-medium text-[#001B38] hover:underline'
            >
              View details →
            </button>
          </div>
        )}
      </div>

      {deleteSubmissionPanel}

      {/* Export XML Artifacts */}
      <div className='rounded-lg border border-gray-200 bg-white p-4'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
          <div className='flex items-center gap-3'>
            <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 shrink-0'>
              <FileJson className='h-4 w-4 text-gray-600' />
            </div>
            <div>
              <p className='text-sm font-medium text-gray-900 text-start'>
                Export XML Artifacts
              </p>
              <p className='text-xs text-gray-500'>
                Download all XML artifacts for HMRC recognition
              </p>
            </div>
          </div>
          <button
            type='button'
            onClick={handleExportXmlArtifacts}
            disabled={isExportingXml}
            className='flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 shrink-0 self-start ml-12 sm:ml-0'
          >
            {isExportingXml ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <Download className='h-4 w-4' />
            )}
            {isExportingXml ? 'Exporting...' : 'Export XML'}
          </button>
        </div>
      </div>

      {companiesHousePanel}
    </div>
  ) : null

  const hmrcStatusModal =
    showStatusModal && hmrcStatusResult ? (
      <HmrcStatusModal
        data={hmrcStatusResult}
        onClose={() => setShowStatusModal(false)}
        onRecheck={handlePollHmrcStatus}
        isRechecking={isPollingHmrcStatus}
      />
    ) : null

  const chResultModal =
    showChResultModal && chAccountsResult ? (
      <ResultModal
        title='Companies House Filing'
        subtitle='Accounts filing result'
        data={chAccountsResult}
        onClose={() => setShowChResultModal(false)}
        variant='success'
      />
    ) : null

  const chStatusModalEl =
    showChStatusModal && chStatusResult ? (
      <ResultModal
        title='Companies House Status'
        subtitle='Filing status from Companies House'
        data={chStatusResult}
        onClose={() => setShowChStatusModal(false)}
      />
    ) : null

  // The HMRC delete endpoint iterates over every period (LPOA = both AP1 and
  // AP2). Surface each period's result so the user can see exactly what was
  // acknowledged, and offer a retry path if any period failed.
  const deletionResultModal = (() => {
    if (!showDeletionResultModal || !deletionResult) return null
    const root =
      (deletionResult as Record<string, unknown>).data ?? deletionResult
    const innerData = (root as Record<string, unknown>)?.data
    const r1 = (innerData as Record<string, unknown>) ?? root
    const results = Array.isArray((r1 as Record<string, unknown>).results)
      ? ((r1 as Record<string, unknown>).results as Record<string, unknown>[])
      : null
    const overallSuccess =
      typeof (deletionResult as Record<string, unknown>).success === 'boolean'
        ? (deletionResult as Record<string, unknown>).success === true
        : results
          ? results.every(r => {
              const s = String((r as Record<string, unknown>).status ?? '').toLowerCase()
              return s === 'accepted' || s === 'success' || s === 'completed' || s === 'ok'
            })
          : true

    if (!results || results.length === 0) {
      // Single-period legacy shape — fall back to the generic modal.
      return (
        <ResultModal
          title='Submission Deleted'
          subtitle='Deletion result from HMRC'
          data={deletionResult}
          onClose={() => setShowDeletionResultModal(false)}
          variant={overallSuccess ? 'success' : 'danger'}
        />
      )
    }
    return createPortal(
      <div className='fixed inset-0 z-[9999] flex items-center justify-center p-4'>
        <div
          className='absolute inset-0 bg-black/50'
          onClick={() => setShowDeletionResultModal(false)}
        />
        <div className='relative z-10 w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl'>
          <div className='flex items-center justify-between rounded-t-2xl border-b border-gray-200 bg-white px-6 py-4 shrink-0'>
            <div>
              <h2 className='text-base font-bold text-gray-900'>Submission Deleted</h2>
              <p className='mt-0.5 text-xs text-gray-500'>
                Per-period acknowledgement from HMRC
              </p>
            </div>
            <button
              type='button'
              onClick={() => setShowDeletionResultModal(false)}
              className='flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-500'
            >
              <X className='h-4 w-4' />
            </button>
          </div>
          <div className='overflow-y-auto flex-1 p-6 space-y-3'>
            <div
              className={`flex items-center gap-3 rounded-xl border-2 p-4 ${
                overallSuccess
                  ? 'border-green-200 bg-green-50'
                  : 'border-amber-200 bg-amber-50'
              }`}
            >
              {overallSuccess ? (
                <CheckCircle className='h-5 w-5 text-green-600' />
              ) : (
                <AlertCircle className='h-5 w-5 text-amber-600' />
              )}
              <p
                className={`text-sm font-medium ${
                  overallSuccess ? 'text-green-900' : 'text-amber-900'
                }`}
              >
                {overallSuccess
                  ? 'All periods acknowledged — HMRC has cleared the submission.'
                  : 'Some periods could not be acknowledged. You can retry.'}
              </p>
            </div>
            <div className='space-y-2'>
              {results.map((r, i) => {
                const status = String(
                  (r as Record<string, unknown>).status ?? ''
                )
                const periodIndex =
                  (r as Record<string, unknown>).periodIndex
                const correlationId = String(
                  (r as Record<string, unknown>).correlationId ?? ''
                )
                const message = String(
                  (r as Record<string, unknown>).message ?? ''
                )
                return (
                  <div
                    key={i}
                    className='rounded-lg border border-gray-200 p-3'
                  >
                    <div className='flex items-start justify-between gap-3 mb-1'>
                      <p className='text-sm font-semibold text-gray-900'>
                        Period {String(periodIndex ?? i + 1)}
                      </p>
                      <StatusPill status={status || undefined} />
                    </div>
                    {correlationId && (
                      <p className='text-[11px] text-gray-500 font-mono break-all'>
                        {correlationId}
                      </p>
                    )}
                    {message && (
                      <p className='mt-1 text-[11px] text-gray-700'>{message}</p>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
          <div className='rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-3 shrink-0 flex items-center gap-2'>
            {!overallSuccess && (
              <button
                type='button'
                onClick={() => {
                  setShowDeletionResultModal(false)
                  void handleDeleteSubmission()
                }}
                disabled={isDeletingSubmission}
                className='flex-1 rounded-lg border border-amber-400 bg-amber-50 py-2.5 text-sm font-medium text-amber-900 hover:bg-amber-100 transition-colors disabled:opacity-50'
              >
                Retry failed periods
              </button>
            )}
            <button
              type='button'
              onClick={() => setShowDeletionResultModal(false)}
              className='flex-1 rounded-lg bg-[#001B38] py-2.5 text-sm font-medium text-white hover:bg-[#002a50] transition-colors'
            >
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body
    )
  })()

  const update = (field: keyof CT600SubmissionForm, value: unknown) => {
    onSubmissionChange({ ...submission, [field]: value })
  }

  // Filter out server-side validation errors that the user has already fixed client-side
  const activeValidationErrors = validationErrors.filter(error => {
    const lower = error.toLowerCase()
    if (
      lower.includes('submitter name') &&
      submission.submittedBy.trim() !== ''
    )
      return false
    if (lower.includes('submitter role') && submission.submitterRole)
      return false
    if (
      lower.includes('declaration must be accepted') &&
      submission.declarationAccepted
    )
      return false
    if (lower.includes('submission role') && submission.submitterRole)
      return false
    return true
  })

  // Consider ready if server says ready OR all server errors are resolved client-side
  const effectivelyReady =
    readyForSubmission || activeValidationErrors.length === 0

  const canSubmit =
    submission.declarationAccepted &&
    submission.submittedBy.trim() !== '' &&
    submission.governmentGatewayUserId.trim() !== '' &&
    submission.governmentGatewayPassword.trim() !== '' &&
    hasCalculated &&
    effectivelyReady &&
    !isSubmitting

  // ── Accepted state (from live submission or loaded form) ──
  // 'completed' is the terminal state after the user acknowledges (deletes)
  // an accepted submission. Render the same success view — the receipt PDF
  // remains downloadable per the backend.
  if (
    !amendMode &&
    (submissionResult?.status === 'accepted' ||
      formStatus === 'accepted' ||
      formStatus === 'completed')
  ) {
    return (
      <div className='space-y-6'>
        <div className='rounded-xl border-2 border-green-200 bg-green-50 p-10 text-center'>
          <div className='mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100'>
            <CheckCircle className='h-12 w-12 text-green-600' />
          </div>
          <h2 className='mt-5 text-2xl font-bold text-green-900'>
            Return Accepted by HMRC
          </h2>
          <p className='mx-auto mt-2 max-w-md text-sm text-green-700'>
            Your CT600 corporation tax return has been successfully submitted
            and accepted by HMRC. No further action is required for this filing.
          </p>
          {submissionResult?.correlationId && (
            <div className='mt-5 inline-block rounded-lg bg-green-100 px-5 py-2.5'>
              <span className='text-xs text-green-700'>
                HMRC Correlation ID:{' '}
              </span>
              <span className='text-sm font-mono font-semibold text-green-900'>
                {submissionResult.correlationId}
              </span>
            </div>
          )}
        </div>

        {/* Read-only submission summary */}
        {submission.submittedBy && (
          <div className='rounded-lg border border-gray-200 bg-white p-5'>
            <h3 className='mb-4 text-sm font-semibold text-gray-900'>
              Submission Summary
            </h3>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div>
                <p className='text-xs text-gray-500'>Submitted By</p>
                <p className='mt-1 text-sm font-medium text-gray-900'>
                  {submission.submittedBy}
                </p>
              </div>
              <div>
                <p className='text-xs text-gray-500'>Submitter Role</p>
                <p className='mt-1 text-sm font-medium text-gray-900'>
                  {submission.submitterRole === 'agent'
                    ? 'Tax Agent'
                    : 'Director'}
                </p>
              </div>
              {submission.submitterRole === 'agent' &&
                submission.agentReference && (
                  <div>
                    <p className='text-xs text-gray-500'>Agent Reference</p>
                    <p className='mt-1 text-sm font-medium text-gray-900'>
                      {submission.agentReference}
                    </p>
                  </div>
                )}
            </div>
          </div>
        )}

        {periodStatusPanel}
        <IxbrlDownloadButton />
        <ReceiptDownloadButton />
        {postSubmissionActions}

        {onStartAmendment && (
          <div className='rounded-lg border border-gray-200 bg-white p-5'>
            <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
              <div className='flex items-start gap-3'>
                <PencilLine className='h-5 w-5 flex-shrink-0 text-gray-600 mt-0.5' />
                <div>
                  <p className='text-sm font-medium text-gray-900 text-start'>
                    Need to correct this return?
                  </p>
                  <p className='mt-0.5 text-xs text-gray-500 leading-relaxed text-start'>
                    File an amended CT600. You can usually amend within 12
                    months of the filing deadline. Your accepted return stays
                    on record unless HMRC accepts the amendment.
                  </p>
                </div>
              </div>
              <button
                type='button'
                onClick={onStartAmendment}
                className='flex shrink-0 items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors'
              >
                <PencilLine className='h-4 w-4' />
                Amend this return
              </button>
            </div>
          </div>
        )}

        <div className='rounded-lg border border-green-200 bg-green-50 p-4'>
          <div className='flex items-start gap-3'>
            <FileCheck className='h-5 w-5 flex-shrink-0 text-green-600 mt-0.5' />
            <div>
              <p className='text-sm font-medium text-green-900 text-start'>
                Declaration confirmed
              </p>
              <p className='mt-0.5 text-xs text-green-700 leading-relaxed'>
                The information in this return was declared correct and complete
                at the time of submission.
              </p>
            </div>
          </div>
        </div>
        {hmrcStatusModal}
        {chResultModal}
        {chStatusModalEl}
        {deletionResultModal}
      </div>
    )
  }

  // ── Submitted state (just submitted or loaded form with submitted status) ──
  if (formStatus === 'submitted' && !amendMode) {
    return (
      <div className='space-y-6'>
        <div className='rounded-xl border-2 border-blue-200 bg-blue-50 p-10 text-center'>
          <div className='mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-blue-100'>
            <Send className='h-10 w-10 text-blue-600' />
          </div>
          <h2 className='mt-5 text-2xl font-bold text-blue-900'>
            Return Submitted to HMRC
          </h2>
          <p className='mx-auto mt-2 max-w-md text-sm text-blue-700'>
            Your CT600 corporation tax return has been successfully submitted to
            HMRC. HMRC typically takes some time to review and process
            submissions.
          </p>
          <div className='mt-5 inline-flex items-center gap-2 rounded-lg bg-blue-100 px-5 py-2.5'>
            <Clock className='h-4 w-4 text-blue-600' />
            <span className='text-sm font-medium text-blue-800'>
              Awaiting HMRC response
            </span>
          </div>
          <p className='mx-auto mt-4 max-w-md text-xs text-blue-600'>
            You can come back to check the status at any time, or you will be
            notified once HMRC processes your return.
          </p>
        </div>

        {submission.submittedBy && (
          <div className='rounded-lg border border-gray-200 bg-white p-5'>
            <h3 className='mb-4 text-sm font-semibold text-gray-900'>
              Submission Summary
            </h3>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div>
                <p className='text-xs text-gray-500'>Submitted By</p>
                <p className='mt-1 text-sm font-medium text-gray-900'>
                  {submission.submittedBy}
                </p>
              </div>
              <div>
                <p className='text-xs text-gray-500'>Submitter Role</p>
                <p className='mt-1 text-sm font-medium text-gray-900'>
                  {submission.submitterRole === 'agent'
                    ? 'Tax Agent'
                    : 'Director'}
                </p>
              </div>
              {submission.submitterRole === 'agent' &&
                submission.agentReference && (
                  <div>
                    <p className='text-xs text-gray-500'>Agent Reference</p>
                    <p className='mt-1 text-sm font-medium text-gray-900'>
                      {submission.agentReference}
                    </p>
                  </div>
                )}
            </div>
          </div>
        )}

        {periodStatusPanel}
        <IxbrlDownloadButton />
        <ReceiptDownloadButton />
        {postSubmissionActions}

        <div className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
          <div className='flex items-start gap-3'>
            <FileCheck className='h-5 w-5 flex-shrink-0 text-blue-600 mt-0.5' />
            <div>
              <p className='text-sm font-medium text-blue-900'>
                Declaration confirmed
              </p>
              <p className='mt-0.5 text-xs text-blue-700 leading-relaxed'>
                The information in this return was declared correct and complete
                at the time of submission.
              </p>
            </div>
          </div>
        </div>
        {hmrcStatusModal}
        {chResultModal}
        {chStatusModalEl}
        {deletionResultModal}
      </div>
    )
  }

  // ── Rejected state (from live submission) ──
  if (
    !amendMode &&
    (submissionResult?.status === 'rejected' || formStatus === 'rejected')
  ) {
    return (
      <div className='space-y-6'>
        <div className='rounded-xl border-2 border-red-200 bg-red-50 p-5 sm:p-8 text-center'>
          <AlertCircle className='mx-auto h-12 w-12 sm:h-16 sm:w-16 text-red-600' />
          <h2 className='mt-3 sm:mt-4 text-xl sm:text-2xl font-bold text-red-900'>
            Submission Rejected
          </h2>
          <p className='mt-2 text-xs sm:text-sm text-red-700'>
            {submissionResult?.message ||
              'HMRC has rejected the submission. Please review the errors and try again.'}
          </p>
        </div>

        <div className='rounded-lg border border-gray-200 bg-white p-4 sm:p-5'>
          <div className='flex items-start gap-3 mb-4'>
            <KeyRound className='h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0' />
            <div>
              <h3 className='text-sm font-semibold text-gray-900'>
                Government Gateway Credentials
              </h3>
              <p className='mt-0.5 text-xs text-gray-500'>
                Update your HMRC Government Gateway credentials and retry
                submission.
              </p>
            </div>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-900'>
                Gateway User ID
              </label>
              <input
                type='text'
                value={submission.governmentGatewayUserId}
                onChange={e =>
                  update('governmentGatewayUserId', e.target.value)
                }
                placeholder='e.g. CTUser100'
                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
              />
            </div>
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-900'>
                Gateway Password
              </label>
              <div className='relative'>
                <input
                  type={showGatewayPassword ? 'text' : 'password'}
                  value={submission.governmentGatewayPassword}
                  onChange={e =>
                    update('governmentGatewayPassword', e.target.value)
                  }
                  placeholder='Enter gateway password'
                  className='w-full rounded-lg border border-gray-300 px-4 py-2.5 pr-10 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                />
                <button
                  type='button'
                  onClick={() => setShowGatewayPassword(!showGatewayPassword)}
                  className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                >
                  {showGatewayPassword ? (
                    <EyeOff className='h-4 w-4' />
                  ) : (
                    <Eye className='h-4 w-4' />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {periodStatusPanel}

        {amendedReturnPanel}

        {isLpoa && periodSubmissions.some(p => p.status === 'accepted') && (
          <div className='rounded-lg border border-blue-200 bg-blue-50 p-3'>
            <p className='text-xs text-blue-800'>
              <strong>Idempotent retry:</strong> any periods HMRC has already
              accepted will be skipped automatically. Only the failed periods
              will be re-submitted.
            </p>
          </div>
        )}

        <button
          type='button'
          onClick={onSubmit}
          disabled={!canSubmit}
          className='w-full rounded-lg bg-gray-900 py-3 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
        >
          {isSubmitting ? (
            <span className='inline-flex items-center gap-2'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Retrying Submission...
            </span>
          ) : (
            'Retry Submission'
          )}
        </button>

        {deleteSubmissionPanel}
        {deletionResultModal}
      </div>
    )
  }

  // ── Pre-payment state: payment-only view ──
  // The "Pay" and "Submit to HMRC" actions are deliberately separate. Before
  // payment we don't ask for submitter details, gateway credentials, or the
  // declaration — those are HMRC-bound information that's only relevant once
  // the platform fee is paid. Asking for them upfront is confusing because
  // many users haven't yet decided whether to pay.
  if (!isPaid) {
    return (
      <div className='space-y-6'>
        <div>
          <h2 className='text-xl font-bold text-gray-900'>
            Pay Platform Filing Fee
          </h2>
          <p className='mt-1 text-sm text-gray-600'>
            Pay the Britfiler platform fee to unlock submission to HMRC. Once
            your payment is confirmed, you&apos;ll come back here to enter the
            declaration and Government Gateway credentials.
          </p>
        </div>

        {/* Calculation gate — same gate, just earlier in the flow */}
        {!hasCalculated && (
          <div className='flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4'>
            <AlertCircle className='h-5 w-5 flex-shrink-0 text-amber-600' />
            <p className='text-sm text-amber-800'>
              Please go back to the Review step and calculate the tax before
              paying.
            </p>
          </div>
        )}

        {/* What's included */}
        <div className='rounded-lg border border-gray-200 bg-white p-5'>
          <div className='flex items-start gap-3 mb-4'>
            <CreditCard className='h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0' />
            <div>
              <h3 className='text-sm font-semibold text-gray-900'>
                Britfiler platform fee
                {selectedPlan ? ` — ${selectedPlan} plan` : ''}
              </h3>
              <p className='mt-0.5 text-xs text-gray-500'>
                Covers preparation and filing of your CT600 return through
                Britfiler.
              </p>
            </div>
          </div>
          <ul className='space-y-2'>
            <li className='flex items-start gap-2 text-xs text-gray-700'>
              <CheckCircle className='h-4 w-4 mt-0.5 text-green-600 flex-shrink-0' />
              CT600 return generation, validation, and HMRC submission
            </li>
            <li className='flex items-start gap-2 text-xs text-gray-700'>
              <CheckCircle className='h-4 w-4 mt-0.5 text-green-600 flex-shrink-0' />
              iXBRL accounts and computations bundled with your filing
            </li>
            <li className='flex items-start gap-2 text-xs text-gray-700'>
              <CheckCircle className='h-4 w-4 mt-0.5 text-green-600 flex-shrink-0' />
              HMRC acknowledgement tracking and downloadable receipt
            </li>
          </ul>
        </div>

        {/* Important — not a payment to HMRC */}
        <div className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
          <div className='flex gap-3 text-start'>
            <div>
              <p className='text-sm font-medium text-blue-900'>
                This is the platform fee, not your corporation tax
              </p>
              <p className='mt-1 text-xs text-blue-700 leading-relaxed text-start'>
                Clicking <strong>Pay</strong> will redirect you to a secure
                Stripe checkout for the{' '}
                <strong>Britfiler platform fee</strong>
                {selectedPlan ? ` (${selectedPlan} plan)` : ''}. Any corporation
                tax owed to HMRC is paid separately through your HMRC account.
              </p>
            </div>
          </div>
        </div>

        {/* Pay button */}
        <button
          type='button'
          onClick={onSubmit}
          disabled={!hasCalculated || isSubmitting}
          className='flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 py-3.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
        >
          {isSubmitting ? (
            <>
              <Loader2 className='h-4 w-4 animate-spin' />
              Redirecting to Payment...
            </>
          ) : (
            <>
              <CreditCard className='h-4 w-4' />
              Pay Platform Fee
            </>
          )}
        </button>

        <p className='text-center text-[11px] text-gray-500'>
          You&apos;ll return to this page automatically once payment is
          confirmed.
        </p>
      </div>
    )
  }

  // ── Default state: editable form (paid, not yet submitted) ──
  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-xl font-bold text-gray-900'>
          {amendMode ? 'Declaration & Submit Amendment' : 'Declaration & Submit'}
        </h2>
        <p className='mt-1 text-sm text-gray-600'>
          {amendMode
            ? 'Authorise and submit your amended CT600 return to HMRC.'
            : 'Authorise and submit your CT600 return to HMRC.'}
        </p>
      </div>

      {amendMode && (
        <div className='flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between'>
          <p className='text-sm text-amber-800 text-start'>
            You are amending a return HMRC has already accepted. Check your
            changes in the earlier steps before submitting.
          </p>
          {onCancelAmendment && (
            <button
              type='button'
              onClick={onCancelAmendment}
              className='shrink-0 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100 transition-colors'
            >
              Cancel amendment
            </button>
          )}
        </div>
      )}

      {/* Calculation Check */}
      {!hasCalculated && (
        <div className='flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4'>
          <AlertCircle className='h-5 w-5 flex-shrink-0 text-amber-600' />
          <p className='text-sm text-amber-800'>
            Please go back to the Review step and calculate the tax before
            submitting.
          </p>
        </div>
      )}

      {/* Preview — the actual CT600 box values, so nothing is submitted unseen */}
      {formId && (
        <div className='flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-5'>
          <div className='flex items-start gap-3'>
            <FileSearch className='mt-0.5 h-5 w-5 flex-shrink-0 text-gray-600' />
            <div>
              <h3 className='text-sm font-semibold text-gray-900'>Check your CT600 before you file</h3>
              <p className='mt-0.5 text-xs text-gray-500'>
                See every box exactly as HMRC will receive it. Nothing is sent until you submit.
              </p>
            </div>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <button
              type='button'
              onClick={() => setIsPreviewOpen(true)}
              className='rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50'
            >
              Preview CT600
            </button>
            <DraftCt600Download formId={formId} isLpoa={isLpoa} />
          </div>
        </div>
      )}

      {formId && (
        <CT600PreviewModal
          formId={formId}
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          {...(companyName ? { companyName } : {})}
          {...(periodStart ? { periodStart } : {})}
          {...(periodEnd ? { periodEnd } : {})}
        />
      )}

      {/* Submitter Details */}
      <div className='rounded-lg border border-gray-200 bg-white p-5'>
        <h3 className='mb-4 text-sm font-semibold text-gray-900'>
          Submission Details
        </h3>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <div>
            <div className='mb-2 flex items-center justify-between'>
              <label className='block text-sm font-medium text-gray-900'>
                Submitted By
              </label>
              {directors.length > 0 && (
                <button
                  type='button'
                  onClick={() => {
                    submittedByModeUserToggledRef.current = true
                    setSubmittedByMode(m =>
                      m === 'select' ? 'custom' : 'select'
                    )
                  }}
                  className='text-[11px] font-medium text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline'
                >
                  {submittedByMode === 'select'
                    ? 'Enter a different name'
                    : 'Choose a director'}
                </button>
              )}
            </div>
            {directors.length > 0 && submittedByMode === 'select' ? (
              <div className='relative'>
                <Users className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
                <select
                  value={submission.submittedBy}
                  onChange={e => update('submittedBy', e.target.value)}
                  className='w-full appearance-none rounded-lg border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                >
                  <option value=''>Select a director</option>
                  {directors.map(d => (
                    <option key={d.rawName ?? d.name} value={d.name}>
                      {d.name}
                      {d.officerRole && d.officerRole !== 'director'
                        ? ` (${d.officerRole})`
                        : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <input
                type='text'
                value={submission.submittedBy}
                onChange={e => update('submittedBy', e.target.value)}
                placeholder='Full name of person submitting'
                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
              />
            )}
          </div>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-900'>
              Submitter Role
            </label>
            <select
              value={submission.submitterRole}
              onChange={e => update('submitterRole', e.target.value)}
              className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
            >
              <option value='company'>Director</option>
              <option value='agent'>Tax Agent</option>
            </select>
          </div>
          {submission.submitterRole === 'agent' && (
            <div className='col-span-2'>
              <label className='mb-2 block text-sm font-medium text-gray-900'>
                Agent Reference
              </label>
              <input
                type='text'
                value={submission.agentReference}
                onChange={e => update('agentReference', e.target.value)}
                placeholder='Your agent reference number'
                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
              />
            </div>
          )}
        </div>
      </div>

      {/* Corporation Tax activation warning */}
      <div className='flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4'>
        <AlertCircle className='h-5 w-5 flex-shrink-0 text-amber-600 mt-0.5' />
        <p className='text-sm text-amber-800'>
          Please ensure to activate your corporation tax on your{' '}
          <a
            href='https://www.gov.uk/log-in-register-hmrc-online-services'
            target='_blank'
            rel='noopener noreferrer'
            className='font-semibold underline hover:text-amber-900'
          >
            HMRC account
          </a>{' '}
          before submitting your filing.
        </p>
      </div>

      {/* Government Gateway Credentials */}
      <div className='rounded-lg border border-gray-200 bg-white p-5'>
        <div className='flex items-start gap-3 mb-4'>
          <KeyRound className='h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0' />
          <div>
            <h3 className='text-sm font-semibold text-gray-900'>
              Government Gateway Credentials
            </h3>
            <p className='mt-0.5 text-xs text-gray-500'>
              Enter your HMRC Government Gateway credentials used for
              corporation tax submissions.
            </p>
          </div>
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-900'>
              Gateway User ID
            </label>
            <input
              type='text'
              value={submission.governmentGatewayUserId}
              onChange={e => update('governmentGatewayUserId', e.target.value)}
              placeholder='e.g. CTUser100'
              className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
            />
          </div>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-900'>
              Gateway Password
            </label>
            <div className='relative'>
              <input
                type={showGatewayPassword ? 'text' : 'password'}
                value={submission.governmentGatewayPassword}
                onChange={e =>
                  update('governmentGatewayPassword', e.target.value)
                }
                placeholder='Enter gateway password'
                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 pr-10 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
              />
              <button
                type='button'
                onClick={() => setShowGatewayPassword(!showGatewayPassword)}
                className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
              >
                {showGatewayPassword ? (
                  <EyeOff className='h-4 w-4' />
                ) : (
                  <Eye className='h-4 w-4' />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Declaration */}
      <div className='rounded-lg border-2 border-gray-200 bg-gray-50 p-5'>
        <label className='flex items-start gap-3 cursor-pointer'>
          <input
            type='checkbox'
            checked={submission.declarationAccepted}
            onChange={e => update('declarationAccepted', e.target.checked)}
            className='mt-1 h-5 w-5 rounded border-gray-300 text-gray-900 focus:ring-gray-900'
          />
          <div>
            <span className='text-sm font-semibold text-gray-900'>
              Declaration
            </span>
            <p className='mt-1 text-xs text-gray-600 leading-relaxed'>
              I declare that the information given in this return and any
              supplementary pages is correct and complete to the best of my
              knowledge and belief. I understand that giving false information
              may result in penalties. I authorise HMRC to communicate with the
              agent named above if applicable, and I confirm that I have the
              authority to sign on behalf of the company.
            </p>
          </div>
        </label>
      </div>

      {amendedReturnPanel}

      {/* Platform fee already paid — confirm to user before HMRC submit */}
      <div className='rounded-lg border border-green-200 bg-green-50 p-4'>
        <div className='flex items-center gap-3'>
          <div>
            <p className='text-sm font-medium text-green-900 text-center'>
              Platform fee paid
            </p>
            <p className='mt-0.5 text-xs text-green-700 text-center'>
              Your filing is ready to submit to HMRC.
            </p>
          </div>
        </div>
      </div>

      {/* Validation Errors / Readiness Blockers */}
      {activeValidationErrors.length > 0 && (
        <div className='rounded-lg border-2 border-red-200 bg-red-50 p-5'>
          <div className='flex items-start gap-3 mb-3'>
            <AlertCircle className='h-5 w-5 flex-shrink-0 text-red-600 mt-0.5' />
            <div>
              <h3 className='text-sm font-semibold text-red-900 text-start'>
                Not ready for submission
              </h3>
              <p className='mt-0.5 text-xs text-red-700 text-start'>
                Please fix the following {activeValidationErrors.length}{' '}
                {activeValidationErrors.length === 1 ? 'issue' : 'issues'}{' '}
                before submitting:
              </p>
            </div>
          </div>
          <ul className='space-y-2 ml-8'>
            {activeValidationErrors.map((error, idx) => {
              const targetStep = getStepFromError(error)
              return (
                <li
                  key={idx}
                  className='flex items-start gap-2 text-xs text-red-800 text-start'
                >
                  <span className='mt-1.5 h-1.5 w-1.5 rounded-full bg-red-400 flex-shrink-0' />
                  <div className='flex-1'>
                    <span>{error}</span>
                    {targetStep && targetStep !== 7 && onGoToStep && (
                      <button
                        type='button'
                        onClick={() => onGoToStep(targetStep)}
                        className='ml-2 inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900 underline underline-offset-2'
                      >
                        Go to {stepLabels[targetStep]}
                        <ExternalLink className='h-3 w-3' />
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* Submit Button */}
      <button
        type='button'
        onClick={onSubmit}
        disabled={!canSubmit}
        className='flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 py-3.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
      >
        {isSubmitting ? (
          <>
            <Loader2 className='h-4 w-4 animate-spin' />
            Submitting to HMRC...
          </>
        ) : (
          <>
            <Send className='h-4 w-4' />
            {amendMode
              ? 'Submit Amended Return to HMRC'
              : 'Submit CT600 Return to HMRC'}
          </>
        )}
      </button>

      {companiesHousePanel}
      {chResultModal}
      {chStatusModalEl}
    </div>
  )
}
